import { yupResolver } from "@hookform/resolvers/yup";
import {
  DialogContent,
  Grid,
  TextField,
  FormControlLabel,
  Checkbox,
  DialogActions,
  Button,
  Autocomplete,
} from "@mui/material";
import axios, { AxiosError } from "axios";
import { useSnackbar } from "notistack";
import { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { queryClient } from "../App";
import { API_URL } from "../consts";
import { Accensione, Dispositivi, Risorse, RisorseTipologie } from "../prismaTypes";
import { changeDatesToUTC, memoParseISO } from "../utils/dateUtils";
import * as yup from "yup";
import { Loading } from "../ui/Loading";
import { DatePicker } from "@mui/x-date-pickers";
import { format } from "date-fns";

const giorni = [
  { g: "Lunedì", v: "lunedi" },
  { g: "Martedì", v: "martedi" },
  { g: "Mercoledì", v: "mercoledi" },
  { g: "Giovedì", v: "giovedi" },
  { g: "Venerdì", v: "venerdi" },
  { g: "Sabato", v: "sabato" },
  { g: "Domenica", v: "domenica" },
];

const schema = yup.object({}).required();

export function AccensioneUi(props: { accensione?: Accensione; handleClose: () => void }) {
  const { accensione, handleClose } = props;
  const { enqueueSnackbar } = useSnackbar();
  const [animations, setAnimations] = useState(!accensione);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let timeout = 0;
    if (loaded) timeout = window.setTimeout(() => setAnimations(true), 1);
    return () => {
      if (timeout !== 0) clearTimeout(timeout);
    };
  }, [loaded]);

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<Accensione & { idRisorsa: number }>({
    mode: "all",
    resolver: yupResolver(schema),
  });

  const { mutate: mutateCreate } = useMutation(
    (data: Accensione) => {
      return axios.post(API_URL + "accensioni-prog", data);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries();
        enqueueSnackbar("Accensione Creata", { variant: "success" });
        handleClose();
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(error?.response?.data?.error ?? "Si è verificato un errore!", {
          variant: "error",
        });
      },
    }
  );

  const { mutate: mutateUpdate } = useMutation(
    (data: Accensione) => axios.patch(API_URL + "accensioni-prog/" + accensione?.id ?? -1, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries();
        enqueueSnackbar("Accensione Aggiornata", { variant: "success" });
        handleClose();
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(error?.response?.data?.error ?? "Si è verificato un errore!", {
          variant: "error",
        });
      },
    }
  );

  const onSubmit = (data: Accensione) => {
    changeDatesToUTC(data);
    if (accensione) mutateUpdate(data);
    else mutateCreate(data);
  };

  const { isLoading, data } = useQuery<unknown, AxiosError, { dispositivi: Dispositivi[] }>(
    ["dispositivi/risorseClub"],
    () => axios.get(API_URL + "dispositivi/risorseClub/").then((res) => res.data)
  );

  const { isLoading: isLoading2, data: data2 } = useQuery<
    unknown,
    AxiosError,
    { risorse: (Risorse & { tipologia: RisorseTipologie })[] }
  >(["risorse/findAll"], () => axios.get(API_URL + "risorse/findAll/").then((res) => res.data));

  useEffect(() => {
    if (!accensione) {
      setValue("attivo", true);
      setValue("spegnimento", false);
      setValue("lunedi", false);
      setValue("martedi", false);
      setValue("mercoledi", false);
      setValue("giovedi", false);
      setValue("venerdi", false);
      setValue("sabato", false);
      setValue("domenica", false);
      setValue("dataDal", null);
      setValue("dataFine", null);
    } else {
      setValue("id", accensione.id);
      setValue(
        "idRisorsa",
        data?.dispositivi.find((d) => d.id === accensione.idDispositivo)?.idRisorse ?? 0,
        { shouldValidate: true }
      );
      setValue("idDispositivo", accensione.idDispositivo);
      setValue(
        "oraAccensione",
        accensione.oraAccensione &&
          format(memoParseISO(accensione.oraAccensione.slice(0, -1)), "HH:mm")
      );
      setValue(
        "oraSpegnimento",
        accensione.oraSpegnimento &&
          format(memoParseISO(accensione.oraSpegnimento.slice(0, -1)), "HH:mm")
      );

      setValue("spegnimento", accensione.spegnimento);

      setValue("dataDal", accensione.dataDal);
      setValue("dataFine", accensione.dataFine);
      setValue("attivo", accensione.attivo);

      setValue("lunedi", accensione.lunedi);
      setValue("martedi", accensione.martedi);
      setValue("mercoledi", accensione.mercoledi);
      setValue("giovedi", accensione.giovedi);
      setValue("venerdi", accensione.venerdi);
      setValue("sabato", accensione.sabato);
      setValue("domenica", accensione.domenica);
    }
    setLoaded(true);
  }, [accensione, setValue, data]);

  if (isLoading || isLoading2 || !loaded) return <Loading />;
  if (!animations) return <></>;
  const dispositivi = data?.dispositivi ?? [];

  const idRisorse = getValues("idRisorsa");

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={animations ? "" : "noAnimation"}>
      <DialogContent>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Controller
              name="attivo"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value === true}
                      onChange={(v) => setValue("attivo", v.target.checked)}
                    />
                  }
                  label="Attiva"
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="idRisorsa"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  getOptionLabel={(option) => option?.denominazione || ""}
                  options={data2?.risorse ?? []}
                  onChange={(_event: any, newValue: Risorse | null) =>
                    setValue(`idRisorsa`, newValue?.id ?? -1, { shouldValidate: true })
                  }
                  value={data2?.risorse.find((c) => c.id === field.value) || null}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      {option.denominazione}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      {...field}
                      required
                      label="Risorsa"
                      variant="standard"
                      error={Boolean(errors.idRisorsa)}
                      helperText={errors.idRisorsa?.message ?? " "}
                    />
                  )}
                />
              )}
            />

            <Controller
              name="idDispositivo"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  getOptionLabel={(option) => option?.descrizione || ""}
                  options={dispositivi.filter((d) => d.idRisorse === idRisorse)}
                  onChange={(_event: any, newValue: Dispositivi | null) =>
                    setValue(`idDispositivo`, newValue?.id ?? -1)
                  }
                  value={dispositivi.find((c) => c.id === field.value) || null}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      {option.descrizione}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      {...field}
                      required
                      label="Dispositivo"
                      variant="standard"
                      error={Boolean(errors.idDispositivo)}
                      helperText={errors.idDispositivo?.message ?? " "}
                    />
                  )}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="spegnimento"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value === true}
                      onChange={(v) =>
                        setValue("spegnimento", v.target.checked, { shouldValidate: true })
                      }
                    />
                  }
                  label="Spegnimento"
                />
              )}
            />

            {!getValues("spegnimento") && (
              <Controller
                name="oraAccensione"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    variant="standard"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 60 }}
                    sx={{ width: 100, mt: 1, mr: 2 }}
                    label="Ora Accensione"
                    type="time"
                    error={Boolean(errors.oraAccensione)}
                    helperText={errors.oraAccensione?.message ?? " "}
                  />
                )}
              />
            )}
            <Controller
              name="oraSpegnimento"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  variant="standard"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 60 }}
                  sx={{ width: 100, mt: 1, mr: 2 }}
                  label="Ora Spegnimento"
                  type="time"
                  error={Boolean(errors.oraSpegnimento)}
                  helperText={errors.oraSpegnimento?.message ?? " "}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="dataDal"
              control={control}
              render={({ field }) => (
                <DatePicker
                  {...field}
                  mask={"__/__/____"}
                  label="Dal"
                  onChange={field.onChange}
                  value={field.value}
                  renderInput={(params) => (
                    <TextField
                      variant="standard"
                      required
                      sx={{ mr: 2 }}
                      helperText={errors.dataDal?.message ?? " "}
                      {...params}
                      error={Boolean(errors.dataDal)}
                    />
                  )}
                />
              )}
            />
            <Controller
              name="dataFine"
              control={control}
              render={({ field }) => (
                <DatePicker
                  {...field}
                  mask={"__/__/____"}
                  label="Al"
                  onChange={field.onChange}
                  value={field.value}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      required
                      helperText={errors.dataFine?.message ?? " "}
                      error={Boolean(errors.dataFine)}
                    />
                  )}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            {giorni.map((g) => (
              <Controller
                key={g.v}
                name={g.v as keyof Accensione}
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value === true}
                        onChange={(v) =>
                          setValue(g.v as keyof Accensione, v.target.checked, {
                            shouldValidate: true,
                          })
                        }
                      />
                    }
                    label={g.g}
                  />
                )}
              />
            ))}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} autoFocus>
          Chiudi
        </Button>
        <Button type="submit" autoFocus>
          Salva
        </Button>
      </DialogActions>
    </form>
  );
}
